import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { icons } from '../../../assets/icons';
import SeeProduct from './SeeProduct';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
	addBasket,
	deleteBasket,
	getBasket,
	getCustomerProductsList,
	getListCategoriesProducts,
	updatedbasketTotalQty,
	companiesForCustomer,
} from '../../../api/redux/slices/Customer';
import Pagination from '../../../helpers/components/Pagination';
import { LoaderComponent } from '../../../helpers/components/loader';
import { roundToThreeDecimals, typePrice } from '../../../helpers/modules';
import { showToast } from '../../../components/modal';

const ProductPageClient = () => {
	const { listProducts, total, allCompanies } = useSelector((state) => state.customer);
	const dispatch = useDispatch();
	const params = useParams();
	const [page, setPage] = useState(Number(params.page));
	const [modalOpen, setModalOpen] = useState(false);
	const [indiceProduit, setIndiceProduit] = useState(null);
	const [listCategories, setListCategories] = useState([]);
	const [searchInput, setSearchInput] = useState('');
	const [categroyValue, setCategoryValue] = useState('');
	const [selectedCompany, setSelectedCompany] = useState('');
	const { basketTotalQty } = useSelector((state) => state.customer);
	const { user } = useSelector((state) => state.auth);
	const [isLoading, setIsLoading] = useState(false);
	const accesstoken = localStorage.getItem('accesstoken');

	useEffect(() => {
		dispatch(getListCategoriesProducts({ token: accesstoken }))
			.unwrap()
			.then((res) => setListCategories(res.data));
	}, [dispatch, accesstoken]);

	useEffect(() => {
		if (accesstoken) {
			dispatch(companiesForCustomer({ token: accesstoken }));
		}
	}, [dispatch, accesstoken]);

	const stableDispatch = useCallback(() => {
		setIsLoading(true);
		dispatch(
			getCustomerProductsList({
				token: accesstoken,
				page: searchInput || categroyValue || selectedCompany ? undefined : page,
				search: searchInput,
				categories: categroyValue,
				companyId: selectedCompany || undefined,
			}),
		).finally(() => setIsLoading(false));
	}, [dispatch, page, categroyValue, searchInput, selectedCompany, accesstoken]);

	useEffect(() => {
		if (accesstoken && user?.data?._id) {
			dispatch(getBasket({ token: accesstoken, id: user.data._id }))
				.unwrap()
				.then((res) => {
					res.data.forEach((item) => {
						if (!item.product) {
							dispatch(deleteBasket({ token: accesstoken, id: item._id }));
						}
					});
				});
		}
	}, [accesstoken, dispatch, user.data?._id]);

	useEffect(() => {
		if (accesstoken) stableDispatch();
	}, [stableDispatch, accesstoken]);

	const handleAddToCart = (product_id, quantity, price) => {
		const total = quantity * price;
		dispatch(addBasket({ token: accesstoken, data: { product_id, quantity, total } }))
			.unwrap()
			.then(() => {
				dispatch(updatedbasketTotalQty(basketTotalQty + quantity));
				showToast('Produit ajouté au panier !', 'success');
			});
		setModalOpen(false);
	};

	return (
		<div id='pr-page-client'>
			{/* ── Top Bar ── */}
			<header className='market-header'>
				<div className='market-header__brand'>
					<span className='market-header__icon'>🛍️</span>
					<h1 className='market-header__title'>Produits</h1>
					<span className='market-header__badge'>{total ?? 0} produits</span>
				</div>

				<div className='market-header__controls'>
					{/* Search */}
					<div className='search-box'>
						<icons.FiSearch className='search-box__icon' />
						<input
							type='search'
							placeholder='Rechercher un produit…'
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							className='search-box__input'
						/>
					</div>

					{/* Category filter */}
					<div className='filter-select'>
						<icons.FiTag className='filter-select__icon' />
						<select
							value={categroyValue}
							onChange={(e) => { setCategoryValue(e.target.value); setPage(1); }}
						>
							<option value=''>Toutes catégories</option>
							{listCategories.map((cat) => (
								<option key={cat._id} value={cat._id}>{cat.designation}</option>
							))}
						</select>
					</div>

					{/* Company filter */}
					<div className='filter-select'>
						<icons.FiBriefcase className='filter-select__icon' />
						<select
							value={selectedCompany}
							onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
						>
							<option value=''>Toutes les sociétés</option>
							{allCompanies?.map((company) => (
								<option key={company._id} value={company._id}>{company.name}</option>
							))}
						</select>
					</div>
				</div>

				{/* Cart */}
				<Link to='/client/products/product_cart' className='cart-link'>
					<div className='cart-link__icon-wrap'>
						<icons.RiShoppingCartLine />
						{basketTotalQty > 0 && (
							<span className='cart-link__count'>{basketTotalQty}</span>
						)}
					</div>
					<span className='cart-link__label'>Panier</span>
				</Link>
			</header>

			{/* ── Grid ── */}
			{isLoading ? (
				<div className='loader-wrap'>
					<LoaderComponent margin='8%' />
				</div>
			) : (
				<>
					{listProducts?.length > 0 ? (
						<div className='product-grid'>
							{listProducts.map((product, index) => {
								const price = roundToThreeDecimals(
									product[typePrice(user.data.categories)] * (1 + product.Tva / 100),
								);
								const companyId = product.created_By?.company?._id;
								const companyName = product.created_By?.company?.name || '—';
								const isOutOfStock = product.capacity < 1;

								return (
									<Fragment key={index}>
										<article className={`product-card${isOutOfStock ? ' product-card--disabled' : ''}`}>
											{/* Stock badge */}
											{isOutOfStock && (
												<span className='product-card__stock-badge'>Rupture</span>
											)}

											{/* Image */}
											<div className='product-card__img-wrap'>
												<img
													src={product.image?.secure_url || process.env.REACT_APP_IMAGE_PLACEHOLDER_URL}
													alt={product.designation}
													loading='lazy'
												/>
												{/* Quick-action overlay */}
												<div className='product-card__overlay'>
													<button
														className='overlay-btn overlay-btn--cart'
														disabled={isOutOfStock}
														onClick={() => handleAddToCart(product._id, 1, product[typePrice(user.data.categories)])}
														title='Ajouter au panier'
													>
														<icons.RiShoppingCartLine />
													</button>
													<button
														className='overlay-btn overlay-btn--eye'
														onClick={() => { setIndiceProduit(product._id); setModalOpen(!modalOpen); }}
														title='Voir le produit'
													>
														<icons.BsEye />
													</button>
												</div>
											</div>

											{/* Info */}
											<div className='product-card__body'>
												<p className='product-card__name'>{product.designation ?? '—'}</p>
												<p className='product-card__price'>
													{price} <span>TND</span>
												</p>

												{/* Footer row */}
												<div className='product-card__footer'>
													{companyId ? (
														<Link
															to={`/client/company/${companyId}`}
															className='product-card__company-link'
															title={`Voir ${companyName}`}
														>
															<icons.FiBriefcase />
															<span>Société : {companyName}</span>
														</Link>
													) : (
														<span className='product-card__company-link product-card__company-link--plain'>
															<icons.FiBriefcase />
															<span>{companyName}</span>
														</span>
													)}
												</div>
											</div>
										</article>

										{indiceProduit === product._id && (
											<SeeProduct
												product={product}
												modalOpen={modalOpen}
												setModalOpen={setModalOpen}
												priceWithVAT={price}
											/>
										)}
									</Fragment>
								);
							})}
						</div>
					) : (
						<div className='empty-state'>
							<span className='empty-state__icon'>🔍</span>
							<p>Aucun produit trouvé</p>
							<small>Essayez de modifier vos filtres</small>
						</div>
					)}

					{listProducts?.length > 0 && (
						<Pagination page={page} setPage={setPage} pageLink='/client/products/' total={total} />
					)}
				</>
			)}
		</div>
	);
};

export default ProductPageClient;